import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom-v5-compat'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { useSnackbar } from 'notistack'

import { api } from '~/api'
import GiftWithPurchaseForm from '~/promotions/components/GiftWithPurchaseForm'
import type { GiftWithPurchaseCampaignDetail } from '~/promotions/schemas'
import { useAppSelector } from '~/store'
import { getPath } from '~/utils'

export default function GiftWithPurchaseCreatePage() {
    const { t } = useTranslation(['common', 'gift_with_purchase'])
    const stores = useAppSelector((state) => state.initial.stores)
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()

    const { mutate: createGiftWithPurchaseCampaign, isLoading } =
        api.promotions.createGiftWithPurchaseCampaign.useMutation()

    const [storeId, setStoreId] = useState(() => stores.at(0)?.id.toString() ?? '')
    const [open, setOpen] = useState(() => stores.length > 1)

    const handleSubmit = useCallback(
        (body: GiftWithPurchaseCampaignDetail) => {
            return createGiftWithPurchaseCampaign(
                { body },
                {
                    onSuccess: ({ body }) => {
                        enqueueSnackbar(t('gift_with_purchase:page_create.request_success'), {
                            variant: 'success',
                        })
                        navigate(
                            getPath('giftWithPurchaseEdit', {
                                campaign_id: body.id?.toString() ?? '',
                            })
                        )
                    },
                    onError: (error) => {
                        if (error.body instanceof Error) {
                            enqueueSnackbar(error.body.message, { variant: 'error' })
                        } else {
                            enqueueSnackbar(t('gift_with_purchase:page_create.request_failed'), {
                                variant: 'error',
                            })
                        }
                    },
                }
            )
        },
        [createGiftWithPurchaseCampaign, enqueueSnackbar, navigate, t]
    )

    return (
        <>
            <GiftWithPurchaseForm
                type="create"
                key={storeId}
                storeId={Number(storeId)}
                isSubmitting={isLoading}
                onSubmit={handleSubmit}
            />

            {open && (
                <Dialog fullWidth disableEscapeKeyDown open={open}>
                    <DialogTitle>{t('gift_with_purchase:page_create.select_store')}</DialogTitle>
                    <DialogContent>
                        <FormControl sx={{ mt: 1 }} fullWidth size="small">
                            <InputLabel id="store-label" sx={{ textTransform: 'capitalize' }}>
                                {t('common:store')}
                            </InputLabel>
                            <Select
                                label={t('common:store')}
                                labelId="store-label"
                                size="small"
                                value={storeId}
                                onChange={(e) => setStoreId(e.target.value)}
                            >
                                {stores.map((store) => (
                                    <MenuItem key={store.id} value={store.id}>
                                        {store.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button sx={{ color: 'text.secondary' }} onClick={() => navigate(-1)}>
                            {t('common:cancel')}
                        </Button>
                        <Button
                            disableElevation
                            variant="contained"
                            disabled={!storeId}
                            onClick={() => setOpen(false)}
                        >
                            {t('common:select')}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    )
}
